import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { buildMediaUrl } from '../../../utils/mediaUrl';

const HowItWorksPreview = ({ isAdminPreview = true }) => {
  const [data, setData] = useState({ section: null, steps: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sectionRes, stepsRes] = await Promise.all([
        api.get('/business/how-it-works/section/').catch(() => ({ data: {} })),
        api.get('/business/how-it-works/'),
      ]);
      setData({
        section: sectionRes.data || {},
        steps: stepsRes.data || []
      });
    } catch (err) {
      console.error('Error fetching how it works preview:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Poll for updates every 2 seconds when in admin preview
    if (isAdminPreview) {
      const interval = setInterval(fetchData, 2000);
      return () => clearInterval(interval);
    }
  }, [isAdminPreview]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!data.steps.length) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.5)',
        backgroundColor: '#1e293b',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        No steps added yet. Add steps to see the preview.
      </div>
    );
  }

  const layoutVariant = data.section?.layout_variant || 'cards';
  const scale = isAdminPreview ? 0.8 : 1;
  const bgColor = data.section?.background_color || '#1e293b';

  return (
    <div style={{
      padding: isAdminPreview ? '1.5rem' : '4rem 2rem',
      backgroundColor: bgColor,
      transform: isAdminPreview ? `scale(${scale})` : 'none',
      transformOrigin: 'top center',
      minHeight: isAdminPreview ? '300px' : 'auto'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: isAdminPreview ? '1.5rem' : '2.5rem',
            fontWeight: '700',
            color: '#fff',
            marginBottom: '0.5rem'
          }}>
            {data.section?.title || 'How It Works'}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: isAdminPreview ? '0.875rem' : '1.1rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>

        {/* Cards Layout */}
        {layoutVariant === 'cards' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${isAdminPreview ? '150px' : '250px'}, 1fr))`,
            gap: isAdminPreview ? '1rem' : '2rem'
          }}>
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  padding: isAdminPreview ? '1rem' : '2rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '1rem',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: isAdminPreview ? '-0.5rem' : '-1rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isAdminPreview ? '1.5rem' : '2.5rem',
                  height: isAdminPreview ? '1.5rem' : '2.5rem',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: '700',
                  fontSize: isAdminPreview ? '0.75rem' : '1rem'
                }}>
                  {index + 1}
                </div>
                {step.image && (
                  <img
                    src={buildMediaUrl(step.image)}
                    alt={step.title}
                    style={{
                      width: isAdminPreview ? '50px' : '80px',
                      height: isAdminPreview ? '50px' : '80px',
                      objectFit: 'cover',
                      borderRadius: '0.75rem',
                      margin: '0.75rem auto'
                    }}
                  />
                )}
                <h3 style={{
                  color: '#fff',
                  fontSize: isAdminPreview ? '0.875rem' : '1.25rem',
                  fontWeight: '600',
                  marginTop: '0.75rem',
                  marginBottom: '0.25rem'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: isAdminPreview ? '0.75rem' : '1rem',
                  lineHeight: '1.5'
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Timeline Layout */}
        {layoutVariant === 'timeline' && (
          <div style={{ position: 'relative', maxWidth: '500px', margin: '0 auto' }}>
            <div style={{
              position: 'absolute',
              left: '1rem',
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: 'rgba(99, 102, 241, 0.3)'
            }} />
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  position: 'relative'
                }}
              >
                <div style={{
                  width: isAdminPreview ? '2rem' : '2.5rem',
                  height: isAdminPreview ? '2rem' : '2.5rem',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: '700',
                  fontSize: isAdminPreview ? '0.75rem' : '1rem',
                  flexShrink: 0,
                  zIndex: 1
                }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    color: '#fff',
                    fontSize: isAdminPreview ? '0.875rem' : '1.1rem',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: isAdminPreview ? '0.75rem' : '1rem',
                    lineHeight: '1.5'
                  }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Numbered Layout */}
        {layoutVariant === 'numbered' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '600px', margin: '0 auto' }}>
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: isAdminPreview ? '1rem' : '1.5rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '1rem',
                  alignItems: 'center'
                }}
              >
                <div style={{
                  fontSize: isAdminPreview ? '1.5rem' : '2.5rem',
                  fontWeight: '700',
                  color: '#6366f1',
                  minWidth: isAdminPreview ? '2rem' : '3rem'
                }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 style={{
                    color: '#fff',
                    fontSize: isAdminPreview ? '0.875rem' : '1.1rem',
                    fontWeight: '600',
                    marginBottom: '0.125rem'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: isAdminPreview ? '0.75rem' : '0.95rem',
                    lineHeight: '1.4'
                  }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HowItWorksPreview;
